/**
 * ============================================================
 *  ICHI & ORI PET CARE — ANALYTICS MODULE
 * ============================================================
 *
 *  Purpose:
 *    Centralised tracking for Google Analytics 4 (GA4) and Meta Pixel.
 *    All custom events fire here so it's easy to add new vendors later
 *    (e.g. PostHog already loads independently in index.html).
 *
 *  How it activates:
 *    1) Open /app/frontend/public/index.html
 *    2) Find the comment block: <!-- GA4 -->
 *       Replace G-XXXXXXXXXX with your actual GA4 Measurement ID,
 *       and remove the surrounding HTML comment wrappers ( <!-- ... --> ).
 *    3) Find the comment block: <!-- META PIXEL -->
 *       Replace YOUR_PIXEL_ID with your actual Meta Pixel ID,
 *       and remove the surrounding HTML comment wrappers.
 *    4) Done. No code changes required here — the helpers below
 *       no-op safely until window.gtag / window.fbq are available.
 *
 *  Events emitted by this app:
 *
 *    | Helper                  | GA4 event          | Meta Pixel event (standard) |
 *    | ----------------------- | ------------------ | --------------------------- |
 *    | trackAppointmentSubmit  | appointment_submit | Schedule                    |
 *    | trackContactSubmit      | contact_submit     | Contact                     |
 *    | trackWhatsAppClick      | whatsapp_click     | Contact (custom: source)    |
 *    | trackCallClick          | call_click         | Contact (custom: source)    |
 *
 *  Each helper also passes a `source` parameter (e.g. 'hero', 'footer',
 *  'floating_button', 'appointment_form') so you can build funnels in GA4.
 *
 *  Debugging:
 *    Set window.__ANALYTICS_DEBUG__ = true in the browser console to log
 *    every event to the console (useful while configuring GA4 / Pixel).
 * ============================================================
 */

const isDebug = () =>
  typeof window !== 'undefined' && window.__ANALYTICS_DEBUG__ === true;

const safeGtag = (...args) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag(...args);
    } catch (e) {
      // swallow — analytics must never break the UI
    }
  }
};

const safeFbq = (...args) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    try {
      window.fbq(...args);
    } catch (e) {
      // swallow
    }
  }
};

/**
 * Generic event dispatcher. Fires the same logical event to GA4 and Meta
 * (as a custom event). Use the named helpers below for typed conversions.
 */
export const trackEvent = (eventName, params = {}, metaStandardEvent = null) => {
  if (isDebug()) {
    // eslint-disable-next-line no-console
    console.log('[analytics]', eventName, params, metaStandardEvent ? `(meta:${metaStandardEvent})` : '');
  }

  // GA4
  safeGtag('event', eventName, params);

  // Meta Pixel — fire a standard event if provided (better attribution)
  if (metaStandardEvent) {
    safeFbq('track', metaStandardEvent, params);
  }
  // Always also fire a custom event so you can build custom audiences
  safeFbq('trackCustom', eventName, params);
};

/* -------------------------------------------------------------
 * Named conversion helpers
 * ------------------------------------------------------------- */

export const trackAppointmentSubmit = (data = {}) => {
  trackEvent(
    'appointment_submit',
    {
      service: data.service,
      pet_type: data.pet_type,
      preferred_date: data.preferred_date,
      source: data.source || 'appointment_form',
    },
    'Schedule'
  );
};

export const trackContactSubmit = (data = {}) => {
  trackEvent(
    'contact_submit',
    {
      subject: data.subject || '(none)',
      source: data.source || 'contact_form',
    },
    'Contact'
  );
};

export const trackWhatsAppClick = (source = 'unknown') => {
  trackEvent(
    'whatsapp_click',
    { source, channel: 'whatsapp' },
    'Contact'
  );
};

export const trackCallClick = (source = 'unknown') => {
  trackEvent(
    'call_click',
    { source, channel: 'phone' },
    'Contact'
  );
};

export const trackBookButtonClick = (source = 'unknown') => {
  trackEvent('book_button_click', { source });
};
